/**
 * Transport for the public booking API (BL-1513).
 *
 * Deliberately not the shared Apollo client: that client attaches the
 * candidate session as a `Bearer` token, refreshes the session on 401 and
 * sends query text, none of which fit an API that is reached with a link
 * token, has no session and executes only persisted operations by id.
 *
 * The token is the candidate's only credential. It goes in the
 * `Authorization` header and nowhere else: never in the URL, never in
 * storage, never in a log line or an error message.
 */

const BOOKING_API_PATH = '/public/booking/graphql';

/** A raw token is 160 bits of base64url: 27 characters of that alphabet. */
const BOOKING_TOKEN_PATTERN = /^[A-Za-z0-9_-]{27}$/;

/**
 * Refusal codes the API answers with before any operation runs, plus
 * `UNKNOWN` for a code this page has not been taught.
 */
export enum BookingRefusalCode {
  NOT_FOUND = 'NOT_FOUND',
  OPERATION_NOT_REGISTERED = 'OPERATION_NOT_REGISTERED',
  TOO_MANY_REQUESTS = 'TOO_MANY_REQUESTS',
  INTERNAL_ERROR = 'INTERNAL_ERROR',
  UNKNOWN = 'UNKNOWN',
}

export type BookingTransportResult<TData> =
  | { kind: 'data'; data: TData }
  /** The API answered, but refused. `NOT_FOUND` arrives with HTTP 200. */
  | { kind: 'refused'; code: BookingRefusalCode; status: number }
  /** No usable answer at all: fetch threw, or the body was not JSON. */
  | { kind: 'network' };

interface GraphQlEnvelope {
  data?: unknown;
  errors?: { message?: string; extensions?: { code?: unknown } }[];
}

/**
 * Whether a string from the URL has the shape of a booking token. A token
 * that fails this can never bind, so the page can skip the round trip.
 */
export const isBookingTokenShaped = (token: string | undefined): boolean =>
  token !== undefined && BOOKING_TOKEN_PATTERN.test(token);

/** Read at call time so tests and environments can vary it. */
const bookingApiUrl = (): string => {
  const base = import.meta.env.VITE_BOOKING_API_URL;
  if (!base) {
    throw new Error('VITE_BOOKING_API_URL is not configured');
  }
  return `${base.replace(/\/$/, '')}${BOOKING_API_PATH}`;
};

const KNOWN_CODES: readonly BookingRefusalCode[] = [
  BookingRefusalCode.NOT_FOUND,
  BookingRefusalCode.OPERATION_NOT_REGISTERED,
  BookingRefusalCode.TOO_MANY_REQUESTS,
  BookingRefusalCode.INTERNAL_ERROR,
];

const refusalCodeOf = (envelope: GraphQlEnvelope): BookingRefusalCode => {
  const code = envelope.errors?.[0]?.extensions?.code;
  return KNOWN_CODES.find(known => known === code) ?? BookingRefusalCode.UNKNOWN;
};

const isEnvelope = (body: unknown): body is GraphQlEnvelope =>
  typeof body === 'object' && body !== null;

/**
 * Runs one persisted operation for the candidate holding `token`.
 *
 * Always inspect the result's `kind`: a bad or expired token is a `refused`
 * result with HTTP 200, so `response.ok` alone would report success.
 */
export const callBookingApi = async <TData>(
  operationId: string,
  token: string,
  variables: Record<string, unknown> = {}
): Promise<BookingTransportResult<TData>> => {
  if (!isBookingTokenShaped(token)) {
    // Mirrors the API's own answer to a malformed token, without the call.
    return { kind: 'refused', code: BookingRefusalCode.NOT_FOUND, status: 200 };
  }

  let response: Response;
  try {
    response = await fetch(bookingApiUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Booking ${token}`,
      },
      body: JSON.stringify({ operationId, variables }),
    });
  } catch {
    return { kind: 'network' };
  }

  // From here on we have an HTTP response. The API answers *everything* with
  // a GraphQL envelope, `{ data }` or `{ errors: [{ extensions: { code } }] }`,
  // and the HTTP status alone is not a reliable signal: a bad, expired or
  // unknown token is NOT_FOUND on HTTP 200 (deliberately, so the endpoint
  // can't be used to probe which links exist). So the order below is: parse
  // the body, then trust `errors` over the status, then trust `data`.

  // 1. The body must be JSON. A non-JSON body means a GraphQL resolver never
  //    ran: a proxy, ingress or crash page answered instead. A 5xx of that
  //    kind is still the service failing, so report it as INTERNAL_ERROR
  //    (the page shows a retry); anything else has no code to act on, so it
  //    is treated the same as a dropped connection.
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return response.status >= 500
      ? { kind: 'refused', code: BookingRefusalCode.INTERNAL_ERROR, status: response.status }
      : { kind: 'network' };
  }

  // 2. JSON, but not an object: not something this API produces, so we
  //    cannot read a code or a payload from it. Same outcome as no answer.
  if (!isEnvelope(body)) {
    return { kind: 'network' };
  }

  // 3. `errors` wins over `data` and over the status. This is the branch
  //    that catches the HTTP 200 NOT_FOUND; checking `response.ok` first
  //    would report that as a success. The status is carried along only for
  //    diagnostics, the page decides what to show from the code.
  if (body.errors && body.errors.length > 0) {
    return {
      kind: 'refused',
      code: refusalCodeOf(body),
      status: response.status,
    };
  }

  // 4. No errors and no data is an envelope the contract does not describe.
  //    Rather than hand `undefined` to a caller that expects a union, report
  //    it as a refusal with no known code so it renders as a generic error.
  if (body.data === undefined || body.data === null) {
    return { kind: 'refused', code: BookingRefusalCode.UNKNOWN, status: response.status };
  }

  // 5. A normal answer. `data` holds one root field (e.g. `schedulingOffer`)
  //    whose value is the operation's union; `bookingOperations.ts` picks
  //    the field and validates its `__typename`, which is why the cast is
  //    acceptable here and nowhere else.
  return { kind: 'data', data: body.data as TData };
};
